import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, X, AlertCircle, CheckCircle, 
  Calendar, ArrowRight, AlertTriangle 
} from 'lucide-react';

const slideVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      type: "spring",
      duration: 0.5,
      bounce: 0.2
    }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    transition: { 
      duration: 0.2,
      ease: "easeOut"
    }
  }
};

/**
 * Componente para mostrar notificación de cambios de plan pendientes
 * 
 * @param {Object} props
 * @param {Object} props.cambio - Información del cambio pendiente
 * @param {Function} props.onCancel - Función para cancelar el cambio
 * @param {Function} props.onDismiss - Función para descartar la notificación temporalmente
 */
const PendingPlanChange = ({ cambio, onCancel, onDismiss }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [visible, setVisible] = useState(true);
  
  // Si no hay cambio o no es visible, no renderizar
  if (!cambio || !visible) return null;
  
  // Formateo de fechas
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };
  
  // Calcular días restantes
  const calcularDiasRestantes = () => {
    const hoy = new Date();
    const fechaEfectiva = new Date(cambio.fecha_nueva_fin);
    const diferencia = fechaEfectiva.getTime() - hoy.getTime();
    const dias = Math.ceil(diferencia / (1000 * 3600 * 24));
    return dias;
  };
  
  const diasRestantes = calcularDiasRestantes();
  
  // Manejador para cancelar el cambio
  const handleCancel = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await onCancel(cambio.id);
      
      setSuccess(true);
      setLoading(false);
      
      // Ocultar notificación después de mostrar éxito
      setTimeout(() => {
        setVisible(false);
      }, 3000);
      
    } catch (err) {
      console.error('Error al cancelar cambio:', err);
      setLoading(false);
      setError(err.response?.data?.error || err.message || 'Error al cancelar el cambio');
    }
  };
  
  // Manejador para descartar la notificación
  const handleDismiss = () => {
    setVisible(false);
    if (onDismiss) onDismiss();
  };
  
  return (
    <AnimatePresence>
      {visible && (
        <motion.div 
          className="mb-6 rounded-xl overflow-hidden shadow-md border border-blue-100"
          variants={slideVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {/* Header */}
          <div className="bg-blue-600 text-white px-4 py-3 flex justify-between items-center">
            <h3 className="text-base font-medium flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              Cambio de Plan Programado
            </h3>
            <button 
              onClick={handleDismiss}
              className="rounded-full p-1 hover:bg-white hover:bg-opacity-20 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* Body */}
          <div className="bg-white p-4">
            {success ? (
              <div className="py-4 text-center">
                <div className="mx-auto w-12 h-12 mb-3 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <p className="text-gray-700">
                  Cambio de plan cancelado correctamente.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <div className="bg-blue-50 px-3 py-1 rounded-full text-blue-700 text-sm font-medium">
                    {diasRestantes > 1 
                      ? `${diasRestantes} días restantes` 
                      : diasRestantes === 1 
                        ? '1 día restante' 
                        : 'Cambio inminente'}
                  </div>
                  <div className="text-sm text-gray-500">
                    Efectivo: {formatDate(cambio.fecha_nueva_fin)}
                  </div>
                </div>
                
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h4 className="text-base font-medium text-gray-800">
                      {cambio.plan_anterior_nombre || 'Gratuito'}
                    </h4>
                    <p className="text-xs text-gray-500">Plan actual</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 mx-2" />
                  <div className="text-right">
                    <h4 className="text-base font-medium text-gray-800">
                      {cambio.plan_nuevo_nombre}
                    </h4>
                    <p className="text-xs text-gray-500">Nuevo plan</p>
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 mb-4">
                  Tu plan cambiará automáticamente en la fecha indicada. 
                  Si deseas cancelar este cambio, puedes hacerlo antes de la fecha efectiva.
                </p>
                
                {error && (
                  <div className="mb-4 text-sm text-red-600 bg-red-50 p-2 rounded-lg flex items-start">
                    <AlertTriangle className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
                    <p>{error}</p>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <button
                    onClick={handleDismiss}
                    className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Recordar después
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={loading}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium 
                      ${loading 
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                      } transition-colors`}
                  >
                    {loading ? 'Cancelando...' : 'Cancelar Cambio'}
                  </button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PendingPlanChange;